/**
 * NameTagSystem.js
 * Manages floating player name tags on top of the 3D canvas viewport.
 * Uses 3D-to-2D screen projection and THREE.Raycaster to perform wall occlusion checks.
 */
export class NameTagSystem {
  constructor(containerId = 'canvas-container', maxDistance = 15) {
    this.container = document.getElementById(containerId);
    this.maxDistance = maxDistance;
    this.tags = new Map(); // peerId -> DOMElement
    this.THREE = null;
    this.raycaster = null;

    // Create a dedicated overlay container for the tags if it doesn't exist
    this.overlayContainer = document.getElementById('nametags-overlay');
    if (!this.overlayContainer) {
      this.overlayContainer = document.createElement('div');
      this.overlayContainer.id = 'nametags-overlay';
      // Apply absolute styles to float perfectly above the canvas
      Object.assign(this.overlayContainer.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: '5', // Float above Canvas (z:1) and under Pause (z:10)
        pointerEvents: 'none', // Allow cursor click-throughs
        overflow: 'hidden'
      });
      document.body.appendChild(this.overlayContainer);
    }
  }

  /**
   * Initializes THREE references.
   */
  init(THREE) {
    this.THREE = THREE;
    this.raycaster = new THREE.Raycaster();
  }

  /**
   * Adds a new floating tag for a peer.
   * @param {string} peerId - Unique peer identifier
   * @param {string} nickname - Peer nickname
   */
  addTag(peerId, nickname) {
    this.removeTag(peerId);

    console.log(`[NameTagSystem] Creating tag for explorer: ${nickname} (${peerId})`);

    const tag = document.createElement('div');
    tag.className = 'player-name-tag';
    
    // Aesthetic premium HTML layout matching retro CRT themes
    tag.innerHTML = `
      <div class="tag-body">
        <span class="tag-nick">${nickname}</span>
        <span class="tag-mic-status">
          <img class="tag-mic-icon" src="/control icons/White/volume_none.png" alt="Mic">
        </span>
      </div>
    `;

    // Apply inline style defaults for GPU-accelerated smooth transitions
    Object.assign(tag.style, {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%) scale(0.8)',
      opacity: '0',
      transition: 'opacity 0.2s ease, transform 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
      pointerEvents: 'none'
    });

    this.overlayContainer.appendChild(tag);
    this.tags.set(peerId, tag);
  }

  /**
   * Updates mic icon styling on a peer's tag.
   * @param {string} peerId - Unique peer identifier
   * @param {boolean} isMuted - Microphone mute state
   */
  updateMicState(peerId, isMuted) {
    const tag = this.tags.get(peerId);
    if (!tag) return;

    const micImg = tag.querySelector('.tag-mic-icon');
    const tagBody = tag.querySelector('.tag-body');
    if (micImg && tagBody) {
      if (isMuted) {
        micImg.src = '/control icons/White/volume_none.png';
        tagBody.classList.remove('talking');
      } else {
        micImg.src = '/control icons/White/volume_wave.png';
        tagBody.classList.add('talking');
      }
    }
  }

  /**
   * Removes a peer's tag.
   * @param {string} peerId - Unique peer identifier
   */
  removeTag(peerId) {
    const tag = this.tags.get(peerId);
    if (tag) {
      tag.style.opacity = '0';
      setTimeout(() => {
        try {
          tag.remove();
        } catch (e) {}
      }, 200);
      this.tags.delete(peerId);
    }
  }

  /**
   * Projects remote positions to screen space, handles distance checks and raycast wall occlusion.
   * @param {THREE.Camera} camera - Local three.js camera
   * @param {THREE.Scene} scene - Active three.js scene
   * @param {Map} remotePeersPositions - Map of peerId -> THREE.Vector3 absolute positions
   * @param {THREE.Mesh} ghostMesh - Reference to the ghost cylinder to avoid colliding with it
   */
  update(camera, scene, remotePeersPositions, ghostMesh) {
    if (!this.THREE || !camera || !scene) return;

    const localPos = camera.position;

    for (const [peerId, tagEl] of this.tags.entries()) {
      const remotePos = remotePeersPositions.get(peerId);
      if (!remotePos) {
        tagEl.style.opacity = '0';
        continue;
      }

      // 1. Calculate absolute distance
      const dist = localPos.distanceTo(remotePos);

      // 2. Perform distance boundaries check
      if (dist > this.maxDistance) {
        tagEl.style.opacity = '0';
        tagEl.style.transform = 'translate(-50%, -50%) scale(0.6)';
        continue;
      }

      // 3. Wall/Obstacle Occlusion Check via Raycasting
      // Cast a ray from camera position to the remote player position
      const dirVec = new this.THREE.Vector3().subVectors(remotePos, localPos).normalize();
      this.raycaster.set(localPos, dirVec);

      // Perform intersection check on all objects recursively
      const intersects = this.raycaster.intersectObjects(scene.children, true);
      let isOccluded = false;

      for (let i = 0; i < intersects.length; i++) {
        const hit = intersects[i];
        
        // We only care about objects that are closer than the remote player
        if (hit.distance < dist - 0.2) { // 0.2m threshold buffer
          // Filter out dust points, the ghostly silhouette itself, local helper grids, or ambient light meshes
          const obj = hit.object;
          if (obj.isMesh && obj !== ghostMesh && !obj.name.includes('helper') && obj.visible) {
            isOccluded = true;
            break;
          }
        }
      }

      // 4. Coordinate Projection 3D to 2D
      const projVec = new this.THREE.Vector3().copy(remotePos);
      // Offset vertically to float above typical head level (eye level 1.75m -> place tag at ~2.1m)
      projVec.y += 0.35;
      
      projVec.project(camera);

      // Check if the coordinate is behind the camera plane
      const isBehind = projVec.z > 1;

      if (isBehind || isOccluded) {
        tagEl.style.opacity = '0';
        tagEl.style.transform = 'translate(-50%, -50%) scale(0.7)';
      } else {
        // Map normal NDC coordinates [-1, 1] to screen viewport pixel coordinates
        const screenX = (projVec.x * 0.5 + 0.5) * window.innerWidth;
        const screenY = (-(projVec.y * 0.5) + 0.5) * window.innerHeight;

        tagEl.style.left = `${screenX}px`;
        tagEl.style.top = `${screenY}px`;

        // Smooth scaling effect according to distance (degrades nicely with depth)
        const scale = Math.max(0.65, 1.0 - (dist / this.maxDistance) * 0.45);
        tagEl.style.transform = `translate(-50%, -50%) scale(${scale.toFixed(3)})`;

        // Smooth fading interpolation near the maximum visibility boundary
        let opacity = 1.0;
        if (dist > this.maxDistance * 0.7) {
          const range = this.maxDistance * 0.3;
          opacity = 1.0 - (dist - this.maxDistance * 0.7) / range;
        }
        
        tagEl.style.opacity = Math.max(0.0, Math.min(1.0, opacity)).toFixed(2);
      }
    }
  }

  /**
   * Resets and clears all tags.
   */
  destroy() {
    for (const peerId of this.tags.keys()) {
      this.removeTag(peerId);
    }
    if (this.overlayContainer) {
      this.overlayContainer.remove();
      this.overlayContainer = null;
    }
    console.log('[NameTagSystem] Name tags destroyed.');
  }
}
